<script>
/**
 * Un filtre à choix multiples, avec recherche.
 *
 * Une liste déroulante native ne retient qu'une valeur, et une liste à choix
 * multiple native se manie à la touche Ctrl — invisible pour qui ne la connaît
 * pas. On ouvre donc un panneau de cases à cocher, précédé d'un champ de
 * recherche dès que les options se comptent par dizaines (gouvernorats,
 * secteurs d'activité).
 */
export default {
  name: 'MultiSelectFilter',
  props: {
    /** Les valeurs retenues, en v-model. */
    modelValue: { type: Array, default: () => [] },
    /** [{ value, label }] */
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Tous' },
    /** Au-delà de ce nombre d'options, un champ de recherche apparaît. */
    searchThreshold: { type: Number, default: 8 },
    disabled: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      open: false,
      search: '',
    };
  },
  computed: {
    selected() {
      return Array.isArray(this.modelValue) ? this.modelValue : [];
    },

    label() {
      if (!this.selected.length) {
        return this.placeholder;
      }

      if (this.selected.length === 1) {
        const match = this.options.find(
          (option) => option.value === this.selected[0],
        );
        return match ? match.label : String(this.selected[0]);
      }

      return `${this.selected.length} sélectionnés`;
    },

    showSearch() {
      return this.options.length > this.searchThreshold;
    },

    visibleOptions() {
      const needle = this.search.trim().toLowerCase();

      if (!needle) {
        return this.options;
      }

      return this.options.filter((option) =>
        String(option.label).toLowerCase().includes(needle),
      );
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onEscape);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onEscape);
  },
  methods: {
    toggleOpen() {
      if (this.disabled) {
        return;
      }

      this.open = !this.open;
      this.search = '';
    },

    isChecked(value) {
      return this.selected.includes(value);
    },

    toggleValue(value) {
      const next = this.isChecked(value)
        ? this.selected.filter((item) => item !== value)
        : [...this.selected, value];

      this.$emit('update:modelValue', next);
    },

    clear() {
      this.$emit('update:modelValue', []);
    },

    // Un panneau ouvert qu'un clic ailleurs ne referme pas reste en travers du
    // reste de l'ecran.
    onDocumentClick(event) {
      if (this.open && !this.$el.contains(event.target)) {
        this.open = false;
      }
    },

    onEscape(event) {
      if (event.key === 'Escape') {
        this.open = false;
      }
    },
  },
};
</script>

<template>
  <div class="multi-select" :class="{ 'multi-select--open': open }">
    <button
      type="button"
      class="list-select multi-select__control"
      :class="{ 'multi-select__control--filled': selected.length > 0 }"
      :disabled="disabled"
      :aria-expanded="open ? 'true' : 'false'"
      data-testid="multi-select-control"
      @click="toggleOpen"
    >
      <span class="multi-select__label">{{ label }}</span>
      <i class="ri-arrow-down-s-line"></i>
    </button>

    <div v-if="open" class="multi-select__menu">
      <div v-if="showSearch" class="multi-select__search">
        <i class="ri-search-line"></i>
        <input
          v-model="search"
          type="text"
          placeholder="Filtrer les choix…"
          data-testid="multi-select-search"
        />
      </div>

      <ul class="multi-select__list">
        <li v-if="!visibleOptions.length" class="multi-select__empty">
          Aucun choix ne correspond.
        </li>

        <li v-for="option in visibleOptions" :key="option.value">
          <label class="multi-select__option">
            <input
              type="checkbox"
              :checked="isChecked(option.value)"
              @change="toggleValue(option.value)"
            />
            <span>{{ option.label }}</span>
          </label>
        </li>
      </ul>

      <div class="multi-select__footer">
        <button
          type="button"
          class="multi-select__clear"
          :disabled="!selected.length"
          @click="clear"
        >
          Tout décocher
        </button>
        <button type="button" class="multi-select__done" @click="open = false">
          Fermer
        </button>
      </div>
    </div>
  </div>
</template>
